/**
 * Billing tools of the ops dashboard (work order item 7): the shop file's billing tab (sync from
 * Shopify, trial extension) and the voucher page.
 *
 * JSX-free on purpose, like the rest of `src/lib`: the dashboard's Jest runs in `node` with no
 * jsdom, so every rule the two screens apply — the day bounds, the preview of a new trial end, the
 * voucher form's validation — lives here where it can be tested.
 *
 * **The server is authoritative.** Each rule below mirrors one the API enforces
 * (`apps/api/src/app/internal-admin/dto/ops-billing.dto.ts`, `ops-vouchers.dto.ts`,
 * `ops-vouchers.service.ts`) so an operator hears about a mistake before the round-trip; the API
 * refuses the same input regardless. The one rule deliberately NOT mirrored is the fixed-discount
 * ceiling (at most the cheapest base price the code applies to): the tier prices live in
 * `libs/types`, which the dashboard does not depend on, and a copy here would drift. The API's
 * 400 names the ceiling, and the page shows that message.
 */

import {
  OpsVoucherDiscountType,
  OpsVoucherRedeemability,
  OpsVoucherTier,
  TrialExtensionBlocker,
  type CreateVoucherRequestDto,
} from '../../../../libs/api-client/src/generated/models';
import { isReasonAcceptable } from './shop-file';

// ── Trial extension ─────────────────────────────────────────────────────────

/**
 * Most days one extension may add — `MAX_TRIAL_EXTENSION_DAYS` in the API (work order A16).
 * Shopify itself accepts up to 1000; the cap is ours, because a trial is unlimited invoicing.
 */
export const MAX_TRIAL_EXTENSION_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Catalogue key explaining why no extension is possible, in plain words. */
const BLOCKER_KEYS: Record<TrialExtensionBlocker, string> = {
  [TrialExtensionBlocker.NoSubscription]:
    'opsBilling.trial.blocker.noSubscription',
  [TrialExtensionBlocker.BypassSubscription]:
    'opsBilling.trial.blocker.bypassSubscription',
  [TrialExtensionBlocker.NotActive]: 'opsBilling.trial.blocker.notActive',
  [TrialExtensionBlocker.NotInTrial]: 'opsBilling.trial.blocker.notInTrial',
  [TrialExtensionBlocker.TrialEnded]: 'opsBilling.trial.blocker.trialEnded',
};

/** The explanation of a blocker, as a catalogue key. */
export function trialBlockerKey(blocker: TrialExtensionBlocker): string {
  return BLOCKER_KEYS[blocker];
}

/**
 * The typed day count, or null when it is not a whole number in 1–{@link MAX_TRIAL_EXTENSION_DAYS}.
 * Strict on purpose: `7.5` or `7 days` is a typo, not seven.
 */
export function parseTrialDays(input: string): number | null {
  const trimmed = input.trim();
  if (!/^\d{1,4}$/.test(trimmed)) return null;
  const days = Number(trimmed);
  return days >= 1 && days <= MAX_TRIAL_EXTENSION_DAYS ? days : null;
}

/** Per-field validation of the extension form, as catalogue keys. */
export interface TrialExtensionErrors {
  readonly days?: string;
  readonly reason?: string;
}

/** Validates the extension form. Empty object = submittable. */
export function validateTrialExtension(
  daysInput: string,
  reason: string,
): TrialExtensionErrors {
  return {
    ...(parseTrialDays(daysInput) === null
      ? { days: 'opsBilling.trial.errorDays' }
      : {}),
    ...(isReasonAcceptable(reason) ? {} : { reason: 'common.reasonMin' }),
  };
}

/**
 * The trial end the confirm dialog announces: the current end plus the days.
 *
 * A PREVIEW. Shopify computes the real end as its own `createdAt + trialDays`, which the tab shows
 * after the call; the two agree whenever the local mirror is in sync, which is what the preview
 * assumes.
 *
 * @param currentTrialEnd - The mirrored end (ISO).
 * @param days - Days to add.
 * @returns The expected new end (ISO).
 */
export function previewTrialEnd(currentTrialEnd: string, days: number): string {
  return new Date(
    new Date(currentTrialEnd).getTime() + days * DAY_MS,
  ).toISOString();
}

// ── Subscription sync ───────────────────────────────────────────────────────

/**
 * What the sync endpoint answered, narrowed from its untyped body.
 *
 * The generated client types the response as `void` (the controller returns a plain object with no
 * DTO), so the page reads it through this guard rather than trusting a shape.
 */
export interface SubscriptionSyncOutcome {
  readonly synced: boolean;
  readonly auditLogId: string | null;
  /** Why nothing was synced, e.g. no active subscription on Shopify. */
  readonly reason: string | null;
  /** Set when the local row pointed at a stale Shopify subscription and was repointed. */
  readonly repointedFrom: string | null;
  readonly localStatus: string | null;
  readonly localTier: string | null;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/** The local subscription's status/tier out of the sync answer, if it carries one. */
function localField(
  body: Record<string, unknown>,
  field: string,
): string | null {
  const local = body['localSubscription'];
  if (typeof local !== 'object' || local === null) return null;
  const record: Record<string, unknown> = { ...local };
  return stringOrNull(record[field]);
}

/**
 * Reads the sync endpoint's answer. Anything it does not recognise reads as "not synced", never
 * as a success.
 */
export function parseSyncOutcome(body: unknown): SubscriptionSyncOutcome {
  const record: Record<string, unknown> =
    typeof body === 'object' && body !== null ? { ...body } : {};
  return {
    synced: record['synced'] === true,
    auditLogId: stringOrNull(record['auditLogId']),
    reason: stringOrNull(record['reason']),
    repointedFrom: stringOrNull(record['repointedFrom']),
    localStatus: localField(record, 'status'),
    localTier: localField(record, 'tier'),
  };
}

// ── Vouchers ────────────────────────────────────────────────────────────────

/**
 * Code shape — `VOUCHER_CODE_PATTERN` in the API, checked on the upper-cased value because the
 * billing page upper-cases what a merchant types before looking it up.
 */
export const VOUCHER_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,31}$/;

/** Longest discount duration, in billing cycles — `MAX_VOUCHER_DURATION_INTERVALS` in the API. */
export const MAX_VOUCHER_DURATION_INTERVALS = 36;

/** Longest internal description the API stores. */
export const VOUCHER_DESCRIPTION_MAX_LENGTH = 200;

/** Every tier, in price order, for the tier checkboxes. */
export const VOUCHER_TIERS: readonly OpsVoucherTier[] = [
  OpsVoucherTier.Starter,
  OpsVoucherTier.Professional,
  OpsVoucherTier.Business,
  OpsVoucherTier.Enterprise,
];

/** What the operator has typed into the create form — strings, as the inputs hold them. */
export interface VoucherDraft {
  readonly code: string;
  readonly discountType: OpsVoucherDiscountType;
  readonly discountValue: string;
  /** Empty = the whole life of the subscription. */
  readonly durationLimitInIntervals: string;
  /** `YYYY-MM-DD`, read as UTC. Empty = from now. */
  readonly validFrom: string;
  /** `YYYY-MM-DD`, read as UTC, inclusive. Empty = no end. */
  readonly validUntil: string;
  /** Empty = unlimited. */
  readonly maxUsage: string;
  /** Empty = every tier. */
  readonly applicableTiers: readonly OpsVoucherTier[];
  readonly description: string;
  readonly reason: string;
}

/** An empty create form. */
export function emptyVoucherDraft(): VoucherDraft {
  return {
    code: '',
    discountType: OpsVoucherDiscountType.Percentage,
    discountValue: '',
    durationLimitInIntervals: '',
    validFrom: '',
    validUntil: '',
    maxUsage: '',
    applicableTiers: [],
    description: '',
    reason: '',
  };
}

/** Per-field validation of the create form, as catalogue keys. */
export type VoucherDraftErrors = Partial<Record<keyof VoucherDraft, string>>;

/** The code as it will be stored and matched. */
export function normaliseVoucherCode(code: string): string {
  return code.trim().toUpperCase();
}

/**
 * A decimal with at most two places, as typed, accepting a German comma. Null otherwise — checked
 * on the TEXT, because `19.99 * 100` is not `1999` in floating point.
 */
function parseDecimal(input: string): number | null {
  const trimmed = input.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  return Number(trimmed);
}

/** A positive whole number as typed, or null. */
function parsePositiveInt(input: string): number | null {
  const trimmed = input.trim();
  if (!/^\d{1,7}$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value >= 1 ? value : null;
}

/** A `YYYY-MM-DD` as a UTC instant — start of day, or the last second of it. Null when invalid. */
function parseDay(input: string, endOfDay: boolean): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input)) return null;
  const date = new Date(`${input}T${endOfDay ? '23:59:59' : '00:00:00'}.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Validation result: the errors, and the request body when there are none. */
export interface VoucherDraftResult {
  readonly errors: VoucherDraftErrors;
  readonly payload: CreateVoucherRequestDto | null;
}

/**
 * Validates the create form and builds the request body.
 *
 * @param draft - The form.
 * @param now - Clock, injectable for tests.
 */
export function validateVoucherDraft(
  draft: VoucherDraft,
  now: Date = new Date(),
): VoucherDraftResult {
  const errors: VoucherDraftErrors = {};
  const code = normaliseVoucherCode(draft.code);
  if (!VOUCHER_CODE_PATTERN.test(code)) errors.code = 'vouchers.form.errorCode';

  const value = parseDecimal(draft.discountValue);
  if (value === null || value <= 0) {
    errors.discountValue = 'vouchers.form.errorValue';
  } else if (
    draft.discountType === OpsVoucherDiscountType.Percentage &&
    value > 100
  ) {
    errors.discountValue = 'vouchers.form.errorPercent';
  }

  const duration =
    draft.durationLimitInIntervals.trim() === ''
      ? null
      : parsePositiveInt(draft.durationLimitInIntervals);
  if (
    draft.durationLimitInIntervals.trim() !== '' &&
    (duration === null || duration > MAX_VOUCHER_DURATION_INTERVALS)
  ) {
    errors.durationLimitInIntervals = 'vouchers.form.errorDuration';
  }

  const maxUsage =
    draft.maxUsage.trim() === '' ? null : parsePositiveInt(draft.maxUsage);
  if (draft.maxUsage.trim() !== '' && maxUsage === null) {
    errors.maxUsage = 'vouchers.form.errorMaxUsage';
  }

  const validFrom = draft.validFrom ? parseDay(draft.validFrom, false) : null;
  if (draft.validFrom && !validFrom)
    errors.validFrom = 'vouchers.form.errorDate';
  const validUntil = draft.validUntil ? parseDay(draft.validUntil, true) : null;
  if (draft.validUntil && !validUntil) {
    errors.validUntil = 'vouchers.form.errorDate';
  } else if (validUntil) {
    if (validUntil.getTime() <= now.getTime()) {
      errors.validUntil = 'vouchers.form.errorUntilPast';
    } else if (validFrom && validUntil.getTime() <= validFrom.getTime()) {
      errors.validUntil = 'vouchers.form.errorUntilBeforeFrom';
    }
  }

  if (draft.description.length > VOUCHER_DESCRIPTION_MAX_LENGTH) {
    errors.description = 'vouchers.form.errorDescription';
  }
  if (!isReasonAcceptable(draft.reason)) errors.reason = 'common.reasonMin';

  if (Object.keys(errors).length > 0 || value === null) {
    return { errors, payload: null };
  }

  const description = draft.description.trim();
  return {
    errors,
    payload: {
      code,
      discountType: draft.discountType,
      discountValue: value,
      durationLimitInIntervals: duration,
      ...(validFrom ? { validFrom: validFrom.toISOString() } : {}),
      validUntil: validUntil ? validUntil.toISOString() : null,
      maxUsage,
      applicableTiers: [...draft.applicableTiers],
      ...(description ? { description } : {}),
      reason: draft.reason.trim(),
    },
  };
}

/**
 * A discount as the reader's locale writes it: `20 %` / `20%`, or `$5.00` / `5,00 $`.
 *
 * `Intl` rather than string glue, so German gets its no-break space before `%` and its decimal
 * comma without a catalogue entry per format.
 */
export function formatDiscount(
  type: OpsVoucherDiscountType,
  value: number,
  currency: string,
  intlLocale: string,
): string {
  if (type === OpsVoucherDiscountType.Percentage) {
    return (value / 100).toLocaleString(intlLocale, {
      style: 'percent',
      maximumFractionDigits: 2,
    });
  }
  return formatMoney(value, currency, intlLocale);
}

/** An amount in the billing currency, as the reader's locale writes it. */
export function formatMoney(
  value: number,
  currency: string,
  intlLocale: string,
): string {
  return value.toLocaleString(intlLocale, { style: 'currency', currency });
}

/** Badge tone and label key for a voucher's redeemability. */
export function redeemabilityBadge(value: OpsVoucherRedeemability): {
  readonly tone: 'green' | 'gray' | 'amber' | 'red';
  readonly labelKey: string;
} {
  switch (value) {
    case OpsVoucherRedeemability.Redeemable:
      return { tone: 'green', labelKey: 'vouchers.state.redeemable' };
    case OpsVoucherRedeemability.Inactive:
      return { tone: 'gray', labelKey: 'vouchers.state.inactive' };
    case OpsVoucherRedeemability.NotYetValid:
      return { tone: 'amber', labelKey: 'vouchers.state.notYetValid' };
    case OpsVoucherRedeemability.Expired:
      return { tone: 'gray', labelKey: 'vouchers.state.expired' };
    case OpsVoucherRedeemability.Exhausted:
      return { tone: 'red', labelKey: 'vouchers.state.exhausted' };
  }
}

/**
 * Whether the two usage figures disagree. They can — a redemption whose bookkeeping failed after
 * Shopify accepted the subscription is logged and swallowed — and the page says so rather than
 * picking one.
 */
export function usageFiguresDisagree(voucher: {
  readonly currentUsage: number;
  readonly usageRowCount: number;
}): boolean {
  return voucher.currentUsage !== voucher.usageRowCount;
}
